import type { AnyVariables, OperationContext } from '@urql/core'
import {
    computed,
    isReadonly,
    isRef,
    type MaybeRef,
    type MaybeRefOrGetter,
    ref,
    type Ref,
    shallowRef,
    watchEffect,
    type WatchHandle,
    type WatchStopHandle,
} from 'vue'
import {
    type Client,
    type CombinedError,
    createRequest,
    type DocumentInput,
    type Operation,
    type OperationResult,
    type OperationResultSource,
} from '@urql/core'
import type { Subscription } from 'wonka'
import { pipe, subscribe, onEnd } from 'wonka'
import type {
    GraphQLRequest,
    UseQueryArgs,
    UseQueryResponse,
    UseQueryState,
    UseSubscriptionArgs,
} from '@urql/vue'

const isPlainObject = (value: any): boolean => {
    if (typeof value !== 'object' || value === null) return false
    return (
        value.constructor &&
        Object.getPrototypeOf(value).constructor ===
            Object.prototype.constructor
    )
}

export const isArray = Array.isArray

const unwrap = <T>(maybeRef: MaybeRefOrGetter<T>): T =>
    typeof maybeRef === 'function'
        ? (maybeRef as () => T)()
        : maybeRef != null && isRef(maybeRef)
          ? maybeRef.value
          : maybeRef

const unwrapDeeply = <T>(input: T): T => {
    input = isRef(input) ? (input.value as T) : input

    if (typeof input === 'function') {
        return unwrapDeeply(input()) as T
    }

    if (input && typeof input === 'object') {
        if (isArray(input)) {
            const length = input.length
            const out = new Array(length) as T
            let i = 0
            for (; i < length; i++) {
                // @ts-ignore
                out[i] = unwrapDeeply(input[i])
            }

            return out
        } else if (isPlainObject(input)) {
            const keys = Object.keys(input)
            const length = keys.length
            let i = 0
            let key: string
            const out = {} as T

            for (; i < length; i++) {
                // @ts-ignore
                key = keys[i]
                // @ts-ignore
                out[key] = unwrapDeeply(input[key])
            }

            return out
        }
    }

    return input
}

export const useRequestState = <
    T = any,
    V extends AnyVariables = AnyVariables,
>(): {
    hasNext: Ref<boolean>
    stale: Ref<boolean>
    fetching: Ref<boolean>
    error: Ref<CombinedError | undefined>
    operation: Ref<Operation<T, V> | undefined>
    extensions: Ref<Record<string, any> | undefined>
} => {
    const hasNext: Ref<boolean> = ref(false)
    const stale: Ref<boolean> = ref(false)
    const fetching: Ref<boolean> = ref(false)
    const error: Ref<CombinedError | undefined> = shallowRef()
    const operation: Ref<Operation<T, V> | undefined> = shallowRef()
    const extensions: Ref<Record<string, any> | undefined> = shallowRef()
    return {
        hasNext,
        stale,
        fetching,
        error,
        operation,
        extensions,
    }
}

export const createRequestWithArgs = <
    T = any,
    V extends AnyVariables = AnyVariables,
>(
    args:
        | UseQueryArgs<T, V>
        | UseSubscriptionArgs<T, V>
        | { query: MaybeRef<DocumentInput<T, V>>; variables: V }
): GraphQLRequest<T, V> => {
    return createRequest<T, V>(
        unwrap(args.query),
        unwrapDeeply(args.variables) as V
    )
}

export function useClientState<T = any, V extends AnyVariables = AnyVariables>(
    args: UseQueryArgs<T, V> | UseSubscriptionArgs<T, V>,
    client: Ref<Client>,
    method: keyof Pick<Client, 'executeSubscription' | 'executeQuery'>
): {
    source: Ref<OperationResultSource<OperationResult<T, V>> | undefined>
    isPaused: Ref<boolean>
    pause: () => void
    resume: () => void
    execute: (opts?: Partial<OperationContext>) => void
    teardown: WatchHandle
} {
    const source: Ref<
        OperationResultSource<OperationResult<T, V>> | undefined
    > = shallowRef()

    const isPaused: Ref<boolean> = isRef(args.pause)
        ? args.pause
        : typeof args.pause === 'function'
          ? computed(args.pause)
          : ref(!!args.pause)

    const request = computed(() => createRequestWithArgs(args))

    const requestOptions = computed(() => {
        return 'requestPolicy' in args
            ? {
                  requestPolicy: unwrap(args.requestPolicy),
                  ...unwrap(args.context),
              }
            : {
                  ...unwrap(args.context),
              }
    })

    const pause = () => {
        if (!isReadonly(isPaused)) {
            isPaused.value = true
        }
    }

    const resume = () => {
        if (!isReadonly(isPaused)) {
            isPaused.value = false
        }
    }

    const executeRaw = (opts?: Partial<OperationContext>) => {
        // @ts-ignore
        return client.value[method]<T, V>(request.value, {
            ...requestOptions.value,
            ...opts,
        })
    }

    const execute = (opts?: Partial<OperationContext>) => {
        source.value = executeRaw(opts)
    }

    // it's important to use `watchEffect()` here instead of `watch()`
    // because it listening for reactive variables inside `executeRaw()` function
    const teardown = watchEffect(() => {
        source.value = !isPaused.value ? executeRaw() : undefined
    })

    return {
        source,
        isPaused,
        pause,
        resume,
        execute,
        teardown,
    }
}

export function callUseQuery<T = any, V extends AnyVariables = AnyVariables>(
    args: UseQueryArgs<T, V>,
    client: Ref<Client>,
    stops?: WatchStopHandle[]
): UseQueryResponse<T, V> {
    const data: Ref<T | undefined> = shallowRef()

    const { fetching, operation, extensions, stale, error, hasNext } =
        // eslint-disable-next-line vue-composable/composable-placement
        useRequestState<T, V>()

    const { isPaused, source, pause, resume, execute, teardown } =
        // eslint-disable-next-line vue-composable/composable-placement
        useClientState(args, client, 'executeQuery')

    const teardownQuery = watchEffect(
        (onInvalidate) => {
            if (source.value) {
                fetching.value = true
                stale.value = false

                onInvalidate(
                    pipe(
                        source.value,
                        onEnd(() => {
                            fetching.value = false
                            stale.value = false
                            hasNext.value = false
                        }),
                        subscribe((res) => {
                            data.value = res.data
                            stale.value = !!res.stale
                            fetching.value = false
                            error.value = res.error
                            operation.value = res.operation
                            extensions.value = res.extensions
                            hasNext.value = res.hasNext
                        })
                    ).unsubscribe
                )
            } else {
                fetching.value = false
                stale.value = false
                hasNext.value = false
            }
        },
        {
            // NOTE: This part of the query pipeline is only initialised once and will need
            // to do so synchronously
            flush: 'sync',
        }
    )

    stops && stops.push(teardown, teardownQuery)

    const then: UseQueryResponse<T, V>['then'] = (onFulfilled, onRejected) => {
        let sub: Subscription | void

        const promise = new Promise<UseQueryState<T, V>>((resolve) => {
            if (!source.value) {
                return resolve(state)
            }
            let hasResult = false
            sub = pipe(
                source.value,
                subscribe(() => {
                    if (!state.fetching.value && !state.stale.value) {
                        if (sub) sub.unsubscribe()
                        hasResult = true
                        resolve(state)
                    }
                })
            )
            if (hasResult) sub.unsubscribe()
        })

        return promise.then(onFulfilled, onRejected)
    }

    const state: UseQueryState<T, V> = {
        data,
        stale,
        error,
        operation,
        extensions,
        fetching,
        isPaused,
        hasNext,
        pause,
        resume,
        executeQuery(opts?: Partial<OperationContext>): UseQueryResponse<T, V> {
            execute(opts)
            return { ...state, then }
        },
    }

    return { ...state, then }
}
